import { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  IGetStudent,
  IGetStudentPtInfo,
  IStudent,
  IUpdateStudentInputBody,
} from '../../../models/students.model';
import Button from '../../Button';
import { DatePicker, Select } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { ISubcode } from '../../../models/codes.model';
import { useHistory, useLocation } from 'react-router-dom';
import FileUpload from '../../FileUpload';
import useAxios from '../../../hooks/useAxios';
import { IGetAuth, IGetJwtToken } from '../../../models/auth.model';
import { getJwtToken, switchAuth } from '../../../api/auth.api';
import { getStudent, getStudentPtInfo, updateStudent } from '../../../api/students.api';
import ErrorResponse from '../../ErrorResponse';
import { AxiosPromise } from 'axios';
import useNotification from '../../../hooks/useNotification';
import moment from 'moment';
import SelectSchoolModal from './modals/SelectSchoolModal';
import ResetPasswordModal from './modals/ResetPasswordModal';
import ChangeStudentWebIdModal from './modals/ChangeStudentWebIdModal';
import styled from 'styled-components';
import useAuth from '../../../hooks/useAuth';
import Loader from '../../Loader';
import { unlockAccount } from '../../../api/employees.api';
import { useTranslation } from 'react-i18next';
import { addHyphen, getWebUrl } from '../../../lib/util';
import DownloadProgramModal from './modals/DownloadProgramModal';
import { getCommonCodeLabel } from '../../../lib/store';
import { creatMenuAccessLog } from '../../../api/systemLog.api';

const Block = styled.div`
  table {
    th {
      width: 105px;
    }
  }
`;

type updateStudentBodyKey = keyof IUpdateStudentInputBody;

interface IProps {
  student?: IStudent;
  isEdit: boolean;
  onEditClick(): void;
  studentExecute(promise: AxiosPromise<IGetStudent>): Promise<void>;
  setMemberCode: (memberCode: string) => void;
  setCampusCode: (campusCode: string) => void;
}

const StudentInfoEditor: FC<IProps> = ({
  student,
  isEdit,
  onEditClick,
  studentExecute,
  setMemberCode,
  setCampusCode,
}) => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { successNotification, infoNotification } = useNotification();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const { auth: authData } = useSelector((state: RootState) => state.auth);  
  const { data: auth, isLoading: authIsLoading, error: authError, execute: authExecute } = useAxios<IGetAuth>();
  const { data: updateStudentResult, isLoading: updateStudentIsLoading, error: updateStudentError, execute: updateStudentExecute } = useAxios();
  const { data: res, isLoading, error, execute } = useAxios<IGetStudentPtInfo>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetJwtToken>();
  const { data: unlockRes, isLoading: unlockIsLoading, error: unlockError, execute: unlockExecute } = useAxios();

  const [studentInputReq, setStudentInputReq] = useState<
    IUpdateStudentInputBody | undefined
  >();
  const [studentInfoFile, setStudentInfoFile] = useState<File | null>(null);
  const [imgBase64, setImgBase64] = useState('');
  const [selectSchoolVisible, setSelectSchoolVisible] = useState(false);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [webIdChangeVisible, setWebIdChangeVisible] = useState(false);
  const [scheme, setScheme] = useState('');
  const [downloadProgramVisible, setDownloadProgramVisible] = useState(false);

  // SY0006 = 학년
  const SY0006 = commonCodes.find((item) => item.commonCode === 'SY0006')?.detailCode;

  useEffect(() => {
    if (student) {
      setStudentInputReq({
        ...student,
        ...(student?.profileImg?.attachFileCode && {
          profileImgFileCode: String(student.profileImg.attachFileCode),
        }),
      });

      execute(getStudentPtInfo(student.studentCode));
      execute2(getJwtToken({
        adminMemCode: authData?.memberCode,
        studentMemCode: student?.studentCode,
      }));
    }
  }, [student]);

  useEffect(() => {
    if (auth && !authIsLoading) {
      const url = getWebUrl();
      if (url) window.location.href = url;
    }
  }, [auth, authIsLoading]);

  useEffect(() => {
    if (updateStudentResult && !updateStudentIsLoading) {
      history.push(`/student/student${location.search}&refresh=true`);
      if (student)
        studentExecute(getStudent(student.studentCode, student.campusCode));
        successNotification(t('student.student.message1'));
      onEditClick();
    }
  }, [updateStudentResult, updateStudentIsLoading]);

  useEffect(() => {
    if (unlockRes && !unlockIsLoading) {
      successNotification(t('student.student.message2'));
    }
  }, [unlockRes, unlockIsLoading]);

  // 학생 이름 입력 검증 (입력 중 - 자음/모음 포함 허용)
  const validateNameInput = (value: string): string => {
    // 허용된 문자만 필터링: 한글(완성형, 자음, 모음), 영문, 공백, 마침표, 하이픈, 어퍼스트로피
    const inputPattern = /[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z .\-']/g;
    const matches = value.match(inputPattern);
    return matches ? matches.join('') : '';
  };

  // 학생 이름 저장 전 검증 (완성형 한글만 허용)
  const validateNameBeforeSave = (value: string): boolean => {
    // 완성형 한글, 영문, 공백, 마침표, 하이픈, 어퍼스트로피만 허용
    const savePattern = /^[가-힣a-zA-Z .\-']*$/;
    return savePattern.test(value);
  };

  const onStudentInputChange = (
    { target: { value } }: ChangeEvent<HTMLInputElement>,
    key: updateStudentBodyKey,
  ) => {
    const newStudentReq = { ...studentInputReq };
    
    // 학생 이름 필드인 경우 입력 검증 적용
    if (key === 'studentKorName' || key === 'studentEngName') {
      const validatedValue = validateNameInput(value);
      (newStudentReq ?? {})[key] = validatedValue;
    } else {
      (newStudentReq ?? {})[key] = value;
    }
    
    setStudentInputReq(newStudentReq);
  };

  const onBirthDayChange = (date: string | null) => {
    if (date === null)
      setStudentInputReq({ ...studentInputReq, birthday: undefined });
    else
      setStudentInputReq({
        ...studentInputReq,
        birthday: date,
      });
  };

  const onSiblingClick = (siblingCode: string, siblingCampusCode: string) => {
    setMemberCode(siblingCode);
    setCampusCode(siblingCampusCode);
  };

  const onSetStudentInfoFile = (file: any) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (base64) setImgBase64(base64.toString());
    };
    reader.readAsDataURL(file);
    setStudentInfoFile(file);
  };

  // 간편예약으로 임시 발급된 아이디(CB+숫자8자리)인지 여부
  const isSystemIssuedWebId = (webId?: string) => !!webId && /^CB\d{8}$/.test(webId);

  const onSwitchAuth = (memberCode?: string) => {
    if (!memberCode) return;
    authExecute(switchAuth(memberCode));
  };

  const onUnlockAccount = (memberCode?: string) => {
    if (!memberCode) return;
    unlockExecute(unlockAccount(memberCode));
  };

  const onLocalEditClick = () => {
    if (isEdit) {
      if (!student) return;
      if (!studentInputReq?.studentKorName)
      return infoNotification(t('student.student.message3'));
      
      // 학생 한글 이름 저장 전 검증
      if (!validateNameBeforeSave(studentInputReq.studentKorName)) {
        return infoNotification(t('student.student.message61'));
      }
      
      // 학생 영문 이름 저장 전 검증 (값이 있는 경우만)
      if (studentInputReq.studentEngName && !validateNameBeforeSave(studentInputReq.studentEngName)) {
        return infoNotification(t('student.student.message61'));
      }
      
      if (!studentInputReq?.birthday)
      return infoNotification(t('student.student.message4'));
      if (!studentInputReq?.mobilePhone)
      return infoNotification(t('student.student.message5'));
      if (!studentInputReq?.schoolCode)
        return infoNotification(t('student.student.message6'));
      if (!studentInputReq?.gradeCode)
        return infoNotification(t('student.student.message7'));
      if (!studentInputReq.parentsCode) return;

      const form = new FormData();
      form.append('studentKorName', studentInputReq.studentKorName);
      if (studentInputReq.studentEngName)
        form.append('studentEngName', studentInputReq.studentEngName);
      form.append('birthday', studentInputReq.birthday);
      form.append('mobilePhone', studentInputReq.mobilePhone);
      form.append('schoolCode', studentInputReq.schoolCode);
      form.append('gradeCode', studentInputReq.gradeCode);
      form.append('parentsCode', studentInputReq.parentsCode);
      studentInputReq.profileImgFileCode &&
        form.append('profileImgFileCode', studentInputReq.profileImgFileCode);

      if (studentInfoFile) {
        form.append('profileImgFile', studentInfoFile);
        if (studentInputReq.profileImgFileCode) {
          form.append('profileImgStatus', 'update');
        } else {
          form.append('profileImgStatus', 'create');
        }
      } else {
        form.append('profileImgStatus', 'keep');
      }

      updateStudentExecute(
        updateStudent(student.studentCode, student.campusCode, form),
      );
    } else {
      onEditClick();
    }
  };

  const getStudentImage = () => {
    if (!student) return null;

    if (student.profileImg?.thumbnailLocation) {
      return student.profileImg.thumbnailLocation;
    } else {
      return student.profileImg?.fileLocation;
    }
  };

  const getJwtUrl = (scheme: string) => {
    const token = res2?.data;    
    return `${scheme}://adminLogin?token=${token}&window_width=1280&window_height=800`;
  };

  const getAICareUrl = (url: string) => {
    if (url) {
      if (!student) return null;
      setAICareLog(); //CAC 로그
      window.open(url + '?memberCode=' + student.studentCode, '_blank', 'noopener,noreferrer');
    }
  };

  const setAICareLog = () => {
    const form = new FormData();
    form.append('systemDiv', 'SU');
    form.append('menuName', 'CAC');
    form.append('menuSubName', 'AI Care');
    form.append('memberCode', authData.memberCode);
    if (student?.studentCode) {
      form.append('targetMemberCode', student.studentCode);
    }
    form.append('actionType', 'VIEW');
    
    creatMenuAccessLog(form).catch((error) => {
      console.error('AI Care 로그 전송 실패:', error);
    });
  }

  return (
    <Block>
      { (authIsLoading || updateStudentIsLoading || isLoading || isLoading2 || unlockIsLoading) && <Loader />}
      <ErrorResponse error={authError} />
      <ErrorResponse error={updateStudentError} />
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={unlockError} />

      <div className='comm-bx__between'>
        <h3 className='sub-title'>{t('student.student.text2')}</h3>
      </div>

      <div className='comm-table__area rowType'>
        <div className='profile'>
          <div className='profile-image'>
            {getStudentImage() ? (
              <img
                className="img-preview"
                src={getStudentImage() || undefined}
                alt="student"
              />
            ) : null}
          </div>
        </div>
      <table>
        <colgroup>
          <col width="60px" />
          <col width="*" />
          <col width="110px" />
          <col width="170px" />
        </colgroup>
        <tbody>
          <tr>
            <th>{t('common.label.1')}</th>
            <td colSpan={3}>
              {getCommonCodeLabel('PR0006', student?.eduStepCode || t('student.student.20'), i18n.language)}
            </td>
          </tr>
          <tr>
            <th>{t('student.student.column5')}{isEdit ? '' : '(ID)'}</th>
            <td>
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control"
                  value={
                    isEdit
                      ? studentInputReq?.studentKorName
                      : `${studentInputReq?.studentKorName} (${student?.webId})`
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onStudentInputChange(e, 'studentKorName')
                  }
                  disabled={!isEdit}
                />
                {!isEdit && (
                  <div className='comm-btn__area'>
                    <button
                      className="btn-basic small mint"
                      onClick={() => onSwitchAuth(student?.studentCode)}
                    >
                      {t('auth.login.title')}
                    </button>
                    {isSystemIssuedWebId(student?.webId) && (
                      <button
                        className="btn-basic small mint"
                        onClick={() => setWebIdChangeVisible(true)}
                      >
                        {t('아이디 변경')}
                      </button>
                    )}
                    <button
                      className="btn-basic small mint"
                      onClick={() => setResetPasswordVisible(true)}
                    >
                      {t('auth.login.label2')}
                    </button>
                    {
                      writeAuthYn === 'Y'
                        ? (
                          <button
                            className="btn-basic small mint"
                            onClick={() => onUnlockAccount(student?.studentCode)}

                          >
                            {t('student.student.button2')}
                          </button>
                          )
                        : null
                    }
                  </div>
                )}
              </div>
            </td>
            <th>{t('student.student.column64')}</th>
            <td>{student?.studentCode}</td>
          </tr>
          <tr>
            <th>{t('student.student.column6')}</th>
            <td>
              <input
                type="text"
                className="form-control input-text"
                value={studentInputReq?.studentEngName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onStudentInputChange(e, 'studentEngName')
                }
                disabled={!isEdit}
              />
            </td>
            <th>{t('student.student.placeholder1')}</th>
            <td>
              <input
                type="text"
                className="form-control input-text"
                value={isEdit ? studentInputReq?.mobilePhone : addHyphen(studentInputReq?.mobilePhone)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onStudentInputChange(e, 'mobilePhone')
                }
                disabled={!isEdit}
              />
            </td>
          </tr>
          <tr>
            <th>{t('student.student.column2')}</th>
            <td>
              <div className="comm-btn__area">
              <span className='v-line'>{studentInputReq?.schoolName}</span>
              {isEdit && (
                <Button
                  className="btn-basic small black ml-2"
                  onClick={() => setSelectSchoolVisible(true)}
                  text={t('common.button.search')}
                />
              )}
              </div>
            </td>
            <th>{t('student.student.column3')}</th>
            <td>
              <Select
                className="w-100"
                placeholder={t('student.student.column3')}
                value={studentInputReq?.gradeCode}
                onChange={(value: string) =>
                  setStudentInputReq({ ...studentInputReq, gradeCode: value })
                }
                disabled={!isEdit}
              >
                {SY0006?.map((item: ISubcode) => (
                  <Select.Option key={item.detailCode} value={item.detailCode}>
                    {i18n.language === 'ko' ? item.korName : item.engName}
                  </Select.Option>
                ))}
              </Select>
            </td>
          </tr>
          {isEdit && (
            <tr>
              <th>{t('student.student.column21')}</th>
              <td colSpan={3}>
                <FileUpload
                  file={studentInfoFile}
                  setFile={onSetStudentInfoFile}
                  isAddon
                />
                {imgBase64 && (
                  <img className="img-preview" src={imgBase64} alt="preview" />
                )}
              </td>
            </tr>
          )}
          <tr>
            <th>{t('student.student.column7')}</th>
            <td>
              <div className="date-picker__area">
                <DatePicker
                  className="ml-1"
                  value={
                    studentInputReq?.birthday
                      ? moment(studentInputReq?.birthday)
                      : undefined
                  }
                  onChange={(e) => {
                    if (e === null) onBirthDayChange(null);
                    else onBirthDayChange(moment(e).format('YYYYMMDD'));
                  }}
                  disabled={!isEdit}
                />
              </div>
            </td>
            <th>{t('common.menu.pt')}</th>
            <td>
              {
                res?.data?.examDate
                  ? `${moment(res.data.examDate).format('YYYY.MM.DD')} (${res?.data?.courseName})`
                  : '-'
              }
            </td>
          </tr>
          <tr>
            <th>{t('student.student.column8')}</th>
            <td>
              {student?.siblings.map((item, index) => (
                <span
                  key={`sibling-${index}`}
                  className="mr-2 text-underline pointer"
                  onClick={() => onSiblingClick(item.siblingCode, item.siblingCampusCode)}
                >
                  {item.siblingKorName} ({item.siblingSchoolName}{' '}
                  {item.siblingGradeCode})
                </span>
              ))}
            </td>
            <th>{t('student.student.21')}</th>
            <td>
              {
                student?.eduStepCode === 'MD'
                  ? getCommonCodeLabel('SY0014', student?.publisherCode || '-', i18n.language)
                  : '-'
              }
            </td>
          </tr>
          <tr>
            <th>{t('student.student.column139')}</th>
            <td colSpan={3}>
              <a
                className="mr-3 btn-text"
                href={getJwtUrl('polycanbon')}
                onClick={() => {
                  setScheme('polycanbon');
                  setDownloadProgramVisible(true);
                }}
              >
                READI
              </a>
              <a
                className="mr-3 btn-text"
                href={getJwtUrl('canbalex')}
                onClick={() => {
                  setScheme('canbalex');
                  setDownloadProgramVisible(true);
                }}
              >
                CANB Alex
              </a>
              <a
                className="btn-text"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  getAICareUrl('/student/care');
                }}
              >
                AI CARE
              </a>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <div className='comm-bx__space'>
        <div className="comm-btn__area right">
          <Button
            className={isEdit ? "btn-basic pink" : "btn-basic pink-line"}
            onClick={onLocalEditClick}
            text={isEdit ? t('common.button.save') : t('common.button.edit')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {selectSchoolVisible && (
        <SelectSchoolModal
          visible={selectSchoolVisible}
          setVisible={setSelectSchoolVisible}
          studentInputReq={studentInputReq}
          setStudentInputReq={setStudentInputReq}
        />
      )}
      {resetPasswordVisible && (
        <ResetPasswordModal
          visible={resetPasswordVisible}
          setVisible={setResetPasswordVisible}
          studentCode={student?.studentCode}
        />
      )}
      {webIdChangeVisible && (
        <ChangeStudentWebIdModal
          visible={webIdChangeVisible}
          setVisible={setWebIdChangeVisible}
          studentCode={student?.studentCode}
          onSuccess={() => student && studentExecute(getStudent(student.studentCode, student.campusCode))}
        />
      )}
      {downloadProgramVisible && (
        <DownloadProgramModal
          visible={downloadProgramVisible}
          setVisible={setDownloadProgramVisible}
          scheme={scheme}
        />
      )}
    </Block>
  );
};

export default StudentInfoEditor;
